import React from 'react';
import { FormField } from '@scale/shared/types/params/textcollection';
import { Breadcrumb } from './breadcrumbs';
import { TextButton } from 'design-system';
import { makeStyles } from 'design-system/styles';

interface FormFieldNavProps {
  formFields: FormField[];
  activeFieldId: string;
  setBreadcrumbs: (breadcrumbs: Breadcrumb[]) => void;
  inaccurateFieldIds?: string[];
  onNavigate?: (finished: boolean) => void;
}

const FormFieldNav: React.FC<FormFieldNavProps> = props => {
  const classes = useFormFieldNavStyles();
  const index = props.formFields.findIndex(formField => formField.field_id === props.activeFieldId);
  const total = props.formFields.length;
  const isFirst = index <= 0;
  const onBackIntent = () => {
    if (!isFirst) {
      props.setBreadcrumbs([{ id: props.formFields[index - 1].field_id, index: 0 }]);
    }
    if (props.onNavigate) props.onNavigate(false);
  };
  const isLast = index >= total - 1;
  const onForwardIntent = () => {
    if (!isLast) {
      props.setBreadcrumbs([
        {
          id: props.formFields[index + 1].field_id,
          index: 0,
        },
      ]);
    }
    if (props.onNavigate) props.onNavigate(index + 1 >= total - 1);
  };
  return (
    <div>
      <div className={classes.actions}>
        <TextButton
          type="primary"
          startIcon={<ArrowLeft width="18" height="18" fill="currentColor" />}
          disabled={isFirst}
          onClick={onBackIntent}
        >
          Previous
        </TextButton>
        <div className={classes.centerLabels}>
          <div className={classes.label}>
            {index + 1} of {total}
          </div>
          <div>
            {props.formFields.map((formField, i) => {
              return (
                <FormFieldNavDot
                  key={formField.field_id}
                  isCurrent={i === index}
                  isForInaccurateForm={isFormFieldInaccurate(
                    formField.field_id,
                    props.inaccurateFieldIds,
                  )}
                />
              );
            })}
          </div>
        </div>
        <TextButton
          type="primary"
          endIcon={<ArrowRight width="18" height="18" fill="currentColor" />}
          disabled={isLast}
          onClick={onForwardIntent}
        >
          Next
        </TextButton>
      </div>
    </div>
  );
};

FormFieldNav.displayName = 'FormFieldNav';

const useFormFieldNavStyles = makeStyles({
  actions: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 8,
    paddingBottom: 8,
    paddingLeft: 16,
    paddingRight: 16,
    borderTop: `1px solid #F2F2F2`,
  },
  centerLabels: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    alignSelf: 'stretch',
    maxWidth: '60%',
    paddingLeft: 8,
    paddingRight: 8,
  },
  label: {
    fontSize: 11,
    lineHeight: '16px',
    color: '#808080',
  },
});

const isFormFieldInaccurate = (formFieldId: string, inaccurateFieldIds?: string[]): boolean => {
  return !!inaccurateFieldIds && inaccurateFieldIds.includes(formFieldId);
};

export default FormFieldNav;

interface FormFieldNavDotProps {
  isCurrent: boolean;
  isForInaccurateForm: boolean;
}

const FormFieldNavDot: React.FC<FormFieldNavDotProps> = props => {
  const classes = useFormFieldNavDotStyles();
  const backgroundColor = props.isForInaccurateForm ? '#D4354F' : '#808080';
  const opacity = props.isCurrent ? 1 : props.isForInaccurateForm ? 0.35 : 0.25;
  return <span className={classes.dot} style={{ backgroundColor, opacity }}></span>;
};

const useFormFieldNavDotStyles = makeStyles({
  dot: {
    display: 'inline-block',
    height: 7,
    width: 7,
    marginLeft: 3,
    marginRight: 3,
    borderRadius: '50%',
    transition: 'opacity 150ms ease-out',
  },
});

const ArrowLeft: React.FC<React.SVGAttributes<SVGElement>> = props => {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path d="M13.256 19.0919L12.4235 19.9244C12.071 20.2769 11.501 20.2769 11.1523 19.9244L3.86228 12.6381C3.50978 12.2856 3.50978 11.7156 3.86228 11.3669L11.1523 4.07687C11.5048 3.72438 12.0748 3.72438 12.4235 4.07687L13.256 4.90937C13.6123 5.26562 13.6048 5.84687 13.241 6.19562L8.72229 10.5006H19.4998C19.9986 10.5006 20.3998 10.9019 20.3998 11.4006V12.6006C20.3998 13.0994 19.9986 13.5006 19.4998 13.5006H8.72229L13.241 17.8056C13.6085 18.1544 13.616 18.7356 13.256 19.0919Z" />
    </svg>
  );
};

const ArrowRight: React.FC<React.SVGAttributes<SVGElement>> = props => {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path d="M10.7438 4.90937L11.5763 4.07687C11.9288 3.72438 12.4988 3.72438 12.8475 4.07687L20.1375 11.3631C20.49 11.7156 20.49 12.2856 20.1375 12.6344L12.8475 19.9244C12.495 20.2769 11.925 20.2769 11.5763 19.9244L10.7438 19.0919C10.3875 18.7356 10.395 18.1544 10.7588 17.8056L15.2775 13.5006H4.50001C4.00126 13.5006 3.60001 13.0994 3.60001 12.6006V11.4006C3.60001 10.9019 4.00126 10.5006 4.50001 10.5006H15.2775L10.7588 6.19562C10.3913 5.84687 10.3838 5.26562 10.7438 4.90937Z" />
    </svg>
  );
};
